import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import defaultContent from '../data/siteContent.json';
import defaultPages from '../data/collectionPages.json';
import { loadContent, saveContent } from '../lib/contentStore';
import { fetchStoreSettings } from '../lib/storeData';

// Site text, colours and collection pages come from the Supabase
// site_content table, so every visitor sees what Andre saved.
// The JSON files in src/data are the starting point and the fallback
// when Supabase can't be reached.
//
// The last good copy from Supabase is kept in this browser
// (CACHE_KEY) only so a repeat visit doesn't flash the old JSON text
// for a moment before the fresh copy arrives.

export var CACHE_KEY = 'aml_site_content_cache_v1';

var DEFAULTS = Object.assign({}, defaultContent, { collection_pages: defaultPages });

// These live in the store_settings table (saveStoreSettings), not here.
var STORE_SETTINGS_KEYS = ['store_status', 'free_shipping_threshold', 'store_pause_reopen'];

// Keep only values of the expected shape, so a bad row can never crash a
// page: the three structured keys must be an array / objects, every other
// key (hero_headline, color_primary, ...) must be a string.
export function cleanContent(raw) {
  var out = {};
  Object.keys(raw || {}).forEach(function(key) {
    var value = raw[key];
    if (key === 'collection_pages') {
      if (Array.isArray(value)) out[key] = value.filter(function(p) { return p && typeof p === 'object' && typeof p.slug === 'string'; });
    } else if (key === 'email_templates' || key === 'email_settings') {
      if (value && typeof value === 'object' && !Array.isArray(value)) out[key] = value;
    } else if (typeof value === 'string') {
      out[key] = value;
    }
  });
  return out;
}

function readCache() {
  try {
    var stored = localStorage.getItem(CACHE_KEY);
    return stored ? cleanContent(JSON.parse(stored)) : {};
  } catch (e) {
    return {};
  }
}

function writeCache(content) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(content)); } catch (e) { /* storage full or blocked: not needed */ }
}

// A colour must look like a colour; anything else is skipped.
var COLOUR_PATTERN = /^(#[0-9a-f]{3,8}|(rgb|rgba|hsl|hsla)\([0-9.,%\s]+\)|[a-z]{3,20})$/i;

export function applyColors(data) {
  var root = document.documentElement;
  Object.keys(data).forEach(function(key) {
    if (key.indexOf('color_') !== 0) return;
    var value = String(data[key] || '').trim();
    // A bad colour falls back to the JSON default rather than leaving an old one behind.
    if (!COLOUR_PATTERN.test(value)) value = String(DEFAULTS[key] || '').trim();
    if (!COLOUR_PATTERN.test(value)) { root.style.removeProperty('--' + key.replace(/_/g, '-')); return; }
    root.style.setProperty('--' + key.replace(/_/g, '-'), value);
  });
}

var SiteContentContext = createContext({
  content: DEFAULTS, loading: false, error: null,
  c: function(key, fallback) { return DEFAULTS[key] || fallback || ''; },
  update: function() { return Promise.resolve({ error: null }); },
  updateBulk: function() { return Promise.resolve({ error: null }); },
  save: function() { return Promise.resolve({ error: null }); },
});

export function SiteContentProvider({ children }) {
  // What Supabase (or the cache) says, on top of the JSON defaults.
  var [saved, setSaved] = useState(function() { return Object.assign({}, DEFAULTS, readCache()); });
  // Store status, free-shipping threshold and reopen date come from store_settings.
  var [storeOverrides, setStoreOverrides] = useState({});
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var mounted = useRef(true);

  useEffect(function() {
    mounted.current = true;
    var contentDone = loadContent().then(function(res) {
      if (!mounted.current) return;
      if (res.error) {
        // Contract: customer pages fall back to the src/data JSON.
        setError(res.error);
        setSaved(Object.assign({}, DEFAULTS));
        return;
      }
      var fresh = cleanContent(res.content);
      writeCache(fresh);
      setError(null);
      setSaved(Object.assign({}, DEFAULTS, fresh));
    });

    var settingsDone = fetchStoreSettings().then(function(res) {
      if (!mounted.current || !res.settings) return;
      setStoreOverrides({
        store_status: res.settings.store_status,
        free_shipping_threshold: String(Number(res.settings.free_shipping_threshold)),
        store_pause_reopen: res.settings.pause_reopen_date || ''
      });
    });

    Promise.all([contentDone, settingsDone]).finally(function() {
      if (mounted.current) setLoading(false);
    });
    return function() { mounted.current = false; };
  }, []);

  var content = useMemo(function() {
    return Object.assign({}, saved, storeOverrides);
  }, [saved, storeOverrides]);

  useEffect(function() { applyColors(content); }, [content]);

  // Get a value with fallback
  var c = useCallback(function(key, fallback) {
    return content[key] || fallback || '';
  }, [content]);

  // Save keys to Supabase (admin only). The change shows at once in this
  // tab, and is undone if the save fails. Resolves to { error } with a
  // plain-English message.
  var savedRef = useRef(saved);
  savedRef.current = saved;
  var save = useCallback(function(allUpdates) {
    // Only send what really changed. Admin forms often pass the whole
    // content object; the store_settings keys are saved in their own table.
    var updates = {};
    var storeKeys = {};
    Object.keys(allUpdates || {}).forEach(function(k) {
      if (STORE_SETTINGS_KEYS.indexOf(k) >= 0) {
        // Already saved to store_settings by the caller; show it in this tab now.
        if (allUpdates[k] !== undefined && allUpdates[k] !== null) storeKeys[k] = String(allUpdates[k]);
        return;
      }
      if (JSON.stringify(allUpdates[k]) !== JSON.stringify(savedRef.current[k])) updates[k] = allUpdates[k];
    });
    if (Object.keys(storeKeys).length > 0) {
      setStoreOverrides(function(prev) { return Object.assign({}, prev, storeKeys); });
    }
    if (Object.keys(updates).length === 0) return Promise.resolve({ error: null });

    var before = {};
    Object.keys(updates).forEach(function(k) { before[k] = savedRef.current[k]; });
    setSaved(function(prev) { return Object.assign({}, prev, updates); });
    return saveContent(updates).then(function(res) {
      if (res.error) {
        console.error('Site content not saved:', res.error);
        if (mounted.current) {
          setSaved(function(prev) {
            var next = Object.assign({}, prev);
            Object.keys(before).forEach(function(k) {
              // Only undo keys nobody has changed since.
              if (next[k] === updates[k]) {
                if (before[k] === undefined) delete next[k]; else next[k] = before[k];
              }
            });
            return next;
          });
        }
      }
      return res;
    });
  }, []);

  // Older names the admin screens use (steps 11 and 12 add the toasts).
  var update = useCallback(function(key, value) {
    var one = {};
    one[key] = value;
    return save(one);
  }, [save]);

  var value = useMemo(function() {
    return { content: content, loading: loading, error: error, c: c, save: save, update: update, updateBulk: save };
  }, [content, loading, error, c, save, update]);

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}

// ---- Collection pages (site_content.collection_pages) ----

function sortPages(pages) {
  return pages.slice().sort(function(a, b) { return (a.sort_order || 0) - (b.sort_order || 0); });
}

export function useCollectionPages() {
  var ctx = useContext(SiteContentContext);
  var stored = ctx.content && ctx.content.collection_pages;
  return useMemo(function() {
    var pages = stored || [];
    var live = sortPages(pages.filter(function(p) { return p.status === 'live'; }));
    return {
      pages: pages,
      live: live,
      byNav: function(navLabel) { return live.filter(function(p) { return p.parent_nav === navLabel; }); },
      bySlug: function(slug) { return pages.find(function(p) { return p.slug === slug; }) || null; },
    };
  }, [stored]);
}
